import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import { EditTextareaField } from '../../../EditTextareaField';
import type { ContactAddressHome } from '../queries/contactAddressesQuery';
import { GET_CONTACT_ADDRESSES } from '../queries/contactAddressesQuery';

const UPSERT_CONTACT_PRIMARY_ADDRESS = gql`
  mutation UpsertContactPrimaryAddress(
    $contactId: ID!
    $values: ContactPrimaryAddressUpsertInput!
  ) {
    upsertContactPrimaryAddress(contactId: $contactId, values: $values) {
      ok
      created
      contactPrimaryAddress {
        id
        homeCity
        homeAddress
        homeStreet
        address2Street2
        address2Street3
        homeState
        homePostalCode
        homeCountryRegion
        homePhone
        homePhone2
        homePhoneSecond
        homeFax2
      }
    }
  }
`;

export interface EditHomeAddressDialogProps {
  contactId: string;
  initialValue: ContactAddressHome;
  isOpen: boolean;
  onChange: (data: ContactAddressHome) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactAddressHome;
}

export function EditHomeAddressDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditHomeAddressDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [upsertAddress] = useMutation(UPSERT_CONTACT_PRIMARY_ADDRESS, {
    refetchQueries: [
      { query: GET_CONTACT_ADDRESSES, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertAddress({
        variables: { contactId, values: changes }
      });

      const data = response.data as {
        upsertContactPrimaryAddress?: { ok?: boolean };
      };

      if (data.upsertContactPrimaryAddress?.ok === false) {
        toast.error('Failed to save home address');
      } else {
        toast.success('Home address saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving home address:', error);
      toast.error('Failed to save home address');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Home Address"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="Street 1"
          onChange={street1 => {
            onChange({ ...value, street1 });
          }}
          value={value.street1}
        />
        <EditFormField
          label="Street 2"
          onChange={street2 => {
            onChange({ ...value, street2 });
          }}
          value={value.street2}
        />
        <EditFormField
          label="Street 3"
          onChange={street3 => {
            onChange({ ...value, street3 });
          }}
          value={value.street3}
        />
        <EditFormField
          label="City"
          onChange={city => {
            onChange({ ...value, city });
          }}
          value={value.city}
        />
        <EditFormField
          label="State/Province"
          onChange={stateProvince => {
            onChange({ ...value, stateProvince });
          }}
          value={value.stateProvince}
        />
        <EditFormField
          label="ZIP/Postal Code"
          onChange={postalCode => {
            onChange({ ...value, postalCode });
          }}
          value={value.postalCode}
        />
        <EditFormField
          label="Country/Region"
          onChange={countryRegion => {
            onChange({ ...value, countryRegion });
          }}
          value={value.countryRegion}
        />
        <EditFormField
          label="Phone"
          onChange={phone => {
            onChange({ ...value, phone });
          }}
          type="tel"
          value={value.phone}
        />
        <EditFormField
          label="Phone 2"
          onChange={phone2 => {
            onChange({ ...value, phone2 });
          }}
          type="tel"
          value={value.phone2}
        />
        <EditFormField
          label="Phone Second"
          onChange={phoneSecond => {
            onChange({ ...value, phoneSecond });
          }}
          type="tel"
          value={value.phoneSecond}
        />
        <EditFormField
          label="Fax 2"
          onChange={fax2 => {
            onChange({ ...value, fax2 });
          }}
          value={value.fax2}
        />
        <div className="col-span-2 mt-4">
          <EditTextareaField
            label="Full Address"
            onChange={fullAddress => {
              onChange({ ...value, fullAddress });
            }}
            value={value.fullAddress}
          />
        </div>
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactAddressHome,
  initial: ContactAddressHome
): Record<string, null | string> {
  const changes: Record<string, null | string> = {};

  if (current.street1 !== initial.street1)
    changes.homeStreet = current.street1 || null;
  if (current.street2 !== initial.street2)
    changes.address2Street2 = current.street2 || null;
  if (current.street3 !== initial.street3)
    changes.address2Street3 = current.street3 || null;
  if (current.city !== initial.city) changes.homeCity = current.city || null;
  if (current.stateProvince !== initial.stateProvince)
    changes.homeState = current.stateProvince || null;
  if (current.postalCode !== initial.postalCode)
    changes.homePostalCode = current.postalCode || null;
  if (current.countryRegion !== initial.countryRegion)
    changes.homeCountryRegion = current.countryRegion || null;
  if (current.phone !== initial.phone)
    changes.homePhone = current.phone || null;
  if (current.phone2 !== initial.phone2)
    changes.homePhone2 = current.phone2 || null;
  if (current.phoneSecond !== initial.phoneSecond)
    changes.homePhoneSecond = current.phoneSecond || null;
  if (current.fax2 !== initial.fax2) changes.homeFax2 = current.fax2 || null;
  if (current.fullAddress !== initial.fullAddress)
    changes.homeAddress = current.fullAddress || null;

  return changes;
}
